// libraries
import React from 'react';
import styled from 'styled-components';
import { Link as ReactRouterLink } from 'react-router-dom';

// custom components
import Form from 'components/Form';
import Input from 'components/Form/Input';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import Select from 'components/Form/Select/Select';

// styled components
import { SubmitButton, Error, Flex } from 'shared/styledComponents';
import { Title, Text } from 'shared/typography';
import { TextStyles } from 'shared/styles';

// constants
import { STATES_OPTIONS } from 'modules/SignUpWithFooterContactForms/ProviderSignUp/constants';

// styles
import { SelectStyles } from 'components/Form/Select/defaultSelectStyles';
import devices from 'theme/devices';
import colors from 'theme/colors';

// utils
import { getValue } from 'utils/getValue';
import { validationMessages } from 'modules/LogInOrCreateAccount/CreateAccount/types';

const Wrapper = styled.div`
  margin-top: 50px;
`;

const PairedInputs = styled(Flex)`
  justify-content: space-between;
  max-width: 565px;
  margin-bottom: 20px;

  @media ${devices.mobile} {
    flex-direction: column;
    margin-bottom: 0;
  }
`;

const StyledInput = styled(Input)`
  ${TextStyles};
  width: 100%;
  max-width: 270px;
  border: 1px solid ${colors.alto};
  border-radius: 4px;
  padding: 13px 15px;
  margin-right: 10px;
  outline: none;

  :last-child {
    margin-right: 0;
  }

  ::placeholder {
    color: ${colors.lightBrown};
  }

  @media ${devices.mobile} {
    max-width: 310px;
    margin-right: 0;
    margin-bottom: 15px;
  }
`;

const FileInputWrapper = styled.div<any>`
  margin-bottom: 20px;
`;

const CheckboxWrapper = styled(Flex)`
  align-items: center;
  margin-bottom: 20px;
`;

const CheckboxLabel = styled(Text)`
  margin-left: 14px;
  cursor: pointer;
`;

const StyledLink = styled(Title)`
  text-decoration: underline;
`;

const SuccessTitle = styled(Title)`
  margin-top: 20px;
`;

const UploadInput = styled(Input)`
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
  pointer-events: none;
`;

const UploadLabel = styled.label(
  ({ theme: { colors, fonts } }) => `
    display: inline-block;
    min-width: 205px;
    padding: 17px 19px;
    font-family: ${fonts.wigrumRegular};
    font-size: 12px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    text-align: center;
    color: ${colors.white};
    cursor: pointer;
    background-color: ${colors.bismark};
  `,
);

export const StyledTextarea = styled(Textarea)(
  ({ theme: { colors, fonts } }) => `
    width: 100%;
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
    border: 1px solid ${colors.alto};
    background-color: ${colors.white};
    color: ${colors.lightBrown};
    font-family: ${fonts.wigrumLight};
    font-size: 16px;
    font-weight: normal;
    line-height: 24px;
    outline: none;
    resize: none;

    @media ${devices.mobile} {
      margin-bottom: 20px;
    }
  `,
);

interface CreateStoryFormProps {
  onSubmit: (values: any) => void;
  formValidation: (values: any) => any;
  stageOptions: any;
  uploadedImageName: string | undefined;
  onChangeUploadInput: (e: any) => void;
}

const CreateStoryForm = ({
  onSubmit,
  formValidation,
  stageOptions,
  uploadedImageName,
  onChangeUploadInput,
}: CreateStoryFormProps): JSX.Element => (
  <Form onSubmit={onSubmit} validate={formValidation}>
    {({ submitting, submitSucceeded, submitFailed, submitErrors, hasValidationErrors, errors }): JSX.Element => (
      <Wrapper>
        <PairedInputs>
          <StyledInput name="name" type="text" placeholder="Name*" size="small" />
          <StyledInput name="email" type="email" placeholder="Email*" size="small" />
        </PairedInputs>
        <PairedInputs>
          <StyledInput name="city" type="text" placeholder="City" size="small" />
          <Select
            name="state"
            options={STATES_OPTIONS}
            customStyles={SelectStyles}
            placeholder={<div>State</div>}
            dropdownIndicatorColor={colors.primary}
          />
        </PairedInputs>
        <PairedInputs>
          <StyledInput name="instagramHandle" type="text" placeholder="Instagram Handle" size="small" />
        </PairedInputs>
        <PairedInputs>
          <Select
            name="stage"
            options={stageOptions}
            customStyles={SelectStyles}
            placeholder={<div>Stage* (multiple)</div>}
            dropdownIndicatorColor={colors.primary}
            isMulti
          />
        </PairedInputs>
        <FileInputWrapper>
          <UploadInput name="file" onChange={onChangeUploadInput} type="file" id="file" />
          <UploadLabel htmlFor="file">{uploadedImageName || 'Upload a picture'}</UploadLabel>
        </FileInputWrapper>
        <StyledTextarea name="body" rows="10" placeholder="Journey told in your own words (4000 characters or less)*" />
        <CheckboxWrapper>
          <Checkbox name="anonymous" />
          <CheckboxLabel htmlFor="anonymous" as="label">
            Remain Anonymous
          </CheckboxLabel>
        </CheckboxWrapper>
        <CheckboxWrapper>
          <Checkbox name="termsAccepted" />
          <CheckboxLabel>
            I agree to the &nbsp;
            <StyledLink as={ReactRouterLink} to="/terms-of-use" size="small" target="_blank">
              Terms of Use
            </StyledLink>
            &nbsp; and &nbsp;
            <StyledLink as={ReactRouterLink} to="/privacy-policy" size="small" target="_blank">
              Privacy Policy
            </StyledLink>
          </CheckboxLabel>
        </CheckboxWrapper>
        {submitFailed && hasValidationErrors && (
          <Error size="small">
            {!!Object.keys(errors).length &&
              `${getValue(Object.keys(errors)[0], validationMessages)} ${errors[Object.keys(errors)[0]]}`}
          </Error>
        )}
        {submitFailed && submitErrors?.error && <Error size="small">{submitErrors?.error}</Error>}
        <SubmitButton type="submit" disabled={submitting} isLoading={submitting}>
          send
        </SubmitButton>
        {submitSucceeded && <SuccessTitle>thanks</SuccessTitle>}
      </Wrapper>
    )}
  </Form>
);

export default CreateStoryForm;
